import React, { ReactElement, useMemo, useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';

import { Table } from '@raptormaps/table';
import { DropdownMenu } from '@raptormaps/dropdown-menu';
import { IconButton } from '@raptormaps/button';

import { getFarmOrgsQuery } from '../../../api/services/farm-orgs';
import { UpdateOrgAccessToFarm } from './UpdateOrgAccessToFarm';

interface FarmPermissionsTableRow {
  orgName: string;
  /** formatted date as string */
  grantedDate: string;
  canEdit: 'True' | 'False';
  actions: ReactElement;
}

const columns: Array<{ key: keyof FarmPermissionsTableRow; name: string }> = [
  {
    key: 'orgName',
    name: 'Organization Name',
  },
  {
    key: 'grantedDate',
    name: 'Access Gained On',
  },
  {
    key: 'canEdit',
    name: 'Can Edit',
  },
  {
    key: 'actions',
    name: '',
  },
];

interface FarmPermissionsTableProps {
  solarFarmId: number;
  solarFarmOrgId: number;
  orgNameMap: Map<number, string>;
}

export const FarmPermissionsTable = ({
  solarFarmId,
  solarFarmOrgId,
  orgNameMap,
}: FarmPermissionsTableProps) => {
  const [permissionToUpdate, setPermissionToUpdate] = useState<{
    orgId: number;
    orgName: string;
    permissionId: number;
    canEdit: boolean;
  } | null>(null);
  const { data, isLoading } = useQuery({
    ...getFarmOrgsQuery(solarFarmId, solarFarmOrgId),
    select: response => response.data,
  });

  const tableData = useMemo<Array<FarmPermissionsTableRow>>(() => {
    return (
      data?.map(entry => {
        const orgName =
          orgNameMap.get(entry.user_id) ?? entry.user_id.toString();

        return {
          orgName,
          grantedDate: moment(entry.granted_at).format('MM/DD/YYYY'),
          canEdit: entry.role_id === 'editor' ? 'True' : 'False',
          actions: (
            <DropdownMenu
              menuContentProps={{
                align: 'end',
              }}
              items={[
                {
                  variant: 'option',
                  value: 'Edit Permissions',
                  onSelect: () => {
                    setPermissionToUpdate({
                      orgId: entry.user_id,
                      orgName,
                      permissionId: entry.id,
                      canEdit: entry.role_id === 'editor',
                    });
                  },
                },
              ]}
            >
              <IconButton
                icon="EllipsisVertical"
                aria-label="More options"
                variant="tertiary"
                style={{ marginLeft: 'auto' }}
              />
            </DropdownMenu>
          ),
        };
      }) ?? []
    );
  }, [data, orgNameMap]);

  return (
    <>
      <Table
        cols={columns}
        data={tableData ?? []}
        fetchData={() => Promise.resolve()}
        totalRowCount={tableData.length}
        fixedHeight={isLoading}
        tableHeight={800}
        hideControls
      />
      {permissionToUpdate ? (
        <UpdateOrgAccessToFarm
          solarFarmId={solarFarmId}
          solarFarmOrgId={solarFarmOrgId}
          {...permissionToUpdate}
          onClose={() => setPermissionToUpdate(null)}
        />
      ) : null}
    </>
  );
};
